<template>
    <ConfirmDialog
        group="confirmation"
        :pt="{ mask: { style: { background: 'var(--overlay-scrim)' } }, root: { 'aria-labelledby': confirmTitleId } }"
    >
        <template #container="{ message, acceptCallback, rejectCallback }">
            <div ref="confirmContainer" tabindex="-1" class="delete-confirmation-container">
                <div>
                    <div class="trash-icon-container" :class="`halo-${variant(message.iconVariant)}`">
                        <component :is="message.icon" class="w-6 h-6" :style="{ color: `var(--${variant(message.iconVariant)}-mark)` }" />
                    </div>
                </div>

                <div class="w-full">
                    <h3 :id="confirmTitleId" class="dialog-title">{{ message.header }}</h3>

                    <p class="dialog-text">
                        {{ message.message }}
                    </p>
                    <div class="flex items-center justify-end gap-3 mt-8">
                        <PrimeButton class="p-btn-root_secondary" :label="message.rejectLabel" autofocus @click="rejectCallback"></PrimeButton>
                        <PrimeButton :label="message.acceptLabel" :loading="message.isLoading" @click="acceptCallback"></PrimeButton>
                    </div>
                </div>
            </div>
        </template>
    </ConfirmDialog>
</template>

<script setup lang="ts">
import ConfirmDialog from 'primevue/confirmdialog';
import type { ConfirmIconVariant } from '~/types/confirm-dialog';

const VARIANTS: readonly ConfirmIconVariant[] = ['danger', 'success', 'warning'];

// `message` reaches the slot as PrimeVue's own ConfirmationOptions, which models no icon intent, so
// the value is validated here rather than trusted into a CSS custom-property name.
function variant(value: unknown): ConfirmIconVariant {
    return VARIANTS.includes(value as ConfirmIconVariant) ? (value as ConfirmIconVariant) : 'danger';
}

const confirmTitleId = useId();

// Dialog's own focus() only searches the header/content/footer containers, none of which a
// `#container` slot creates, so focus never enters the dialog on its own and Tab keeps walking the
// page behind the mask. The slot renders only while the dialog is open, so the ref's presence is
// also its open state. Escape still closes it through Dialog's own document listener.
const confirmContainer = ref<HTMLElement | null>(null);

useFocusTrap(confirmContainer, computed(() => confirmContainer.value !== null));
</script>

<style lang="scss" scoped>
.delete-confirmation-container:focus:not(:focus-visible) {
    outline: none;
}

.delete-confirmation-container {
    @apply sm:max-w-lg sm:w-[544px] w-11/12 rounded-lg bg-white mx-auto h-auto overflow-y-auto p-6 gap-4 flex;

    .trash-icon-container {
        @apply border-8 rounded-3xl;
    }

    .halo-danger {
        background: var(--danger-wash);
        border-color: color-mix(in srgb, var(--danger-wash) 45%, var(--surface));
    }

    .halo-success {
        background: var(--success-wash);
        border-color: color-mix(in srgb, var(--success-wash) 45%, var(--surface));
    }

    .halo-warning {
        background: var(--warning-wash);
        border-color: color-mix(in srgb, var(--warning-wash) 45%, var(--surface));
    }

    .dialog-text {
        @apply text-sm font-normal text-gray-600;
    }

    .dialog-title {
        @apply text-lg font-semibold text-gray-900 mb-2;
    }
}
</style>
